import HelpIcon from '@mui/icons-material/Help';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ModalActions from 'common/components/Elements/Modal/ModalActions/ModalActions';
import SimpleModal, { ScrollableModalContent } from 'common/components/Elements/Modal/SimpleModal/SimpleModal';
import SanitizeHTML from 'common/components/Elements/SanitizeHTML/SanitizeHTML';
import * as I from 'core/api/coreApiClient';
import classes from 'core/components/Elements/Table/Table/Table.styles';
import { ExternalLink, InternalLink } from 'core/components/Elements/Value/Value';
import type { SxStyles} from 'core/utilities/theme';
import theme, { ThemeColors } from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import type { CSSProperties} from 'react';
import React, { useState } from 'react';
import sanitizeHtml from 'sanitize-html';

export const denotationAsText = (denotation: I.Denotation) => denotation.parts
  .filter((part) => part.value)
  .map((part) => sanitizeHtml(
    part.value || '', { allowedTags: [] },
  ))
  .join(' ');

function DenotationPartModalHTML({ part }: { part: I.Part }) {
  const [open, setOpen] = useState(false);

  if (!part.value) return null;

  return (
    <>
      {open && (
        <SimpleModal
          title={part.label || I18n.Elements.Denotation.DenotationPartModalHTML.title}
          open={open}
          onClose={() => setOpen(false)}
          size="auto"
          content={(
            <Box sx={{ mt: 1 }}>
              <ScrollableModalContent>
                <Box sx={{
                  '& *': { fontFamily: theme.typography.fontFamily },
                  '& p, & span, & li': theme.typography.body2,
                  '& table': { ...classes.table, borderSpacing: 0 } as CSSProperties,
                  '& tbody': classes.tbodyLightTable as CSSProperties,
                  '& th': {
                    padding: '6px 16px',
                    fontWeight: 'bold',
                    borderBottom: `1px solid ${ThemeColors.borderColor}`,
                    fontSize: '0.75rem',
                    lineHeight: '1.66',
                    letterSpacing: '0.03333em',
                  },
                  '& td': {
                    borderBottom: `1px solid ${ThemeColors.borderColor}`,
                    padding: '6px 16px',
                  },
                  '& pre': {
                    fontFamily: 'monospace',
                  },
                }}
                >
                  <SanitizeHTML
                    html={part.value}
                    additionalTags={[
                      'p', 'ul', 'li', 'table', 'th', 'td', 'tr', 'tbody', 'thead', 'tfoot',
                    ]}
                  />
                </Box>
              </ScrollableModalContent>
              <ModalActions noApply />
            </Box>
          )}
        />
      )}

      <IconButton
        onClick={() => setOpen(true)}
        size="small"
        title={I18n.Elements.Denotation.DenotationPartModalHTML.buttonTitle}
        sx={{ p: 0 }}
      >
        <HelpIcon fontSize="small" color="primary" />
      </IconButton>
    </>
  );
}

export interface ComponentProps {
  denotation: I.Denotation;
  ignoreLineBreak?: boolean;
  linkSx?: SxStyles;
}

function Denotation({ denotation, ignoreLineBreak, linkSx }: ComponentProps) {
  const elements = denotation.parts.map((part) => {
    switch (part.type) {
      case I.DenotationPartTypeConstants.String: {
        return part.value || null;
      }
      case I.DenotationPartTypeConstants.Paragraph: {
        return part.value
          ? (<p style={{ display: 'inline-block', margin: 0, padding: 0 }}>{part.value || ''}</p>)
          : (null);
      }
      case I.DenotationPartTypeConstants.LineBreak: {
        return ignoreLineBreak ? null : (<br />);
      }
      case I.DenotationPartTypeConstants.HtmlSafe: {
        return part.value ? <SanitizeHTML html={part.value} additionalTags={['ul', 'li']} /> : null;
      }
      case I.DenotationPartTypeConstants.HtmlUnsafe: {
        return part.value ? <SanitizeHTML html={part.value} /> : null;
      }
      case I.DenotationPartTypeConstants.ExternalLink: {
        return part.value && part.url
          ? <ExternalLink value={part.value} url={part.url} sx={linkSx} />
          : null;
      }
      case I.DenotationPartTypeConstants.InternalLink: {
        return part.value && part.url
          ? <InternalLink value={part.value} url={part.url} sx={linkSx} />
          : null;
      }
      case I.DenotationPartTypeConstants.ModalHTML: {
        return part.value ? <DenotationPartModalHTML part={part} /> : null;
      }
      default: {
        return null;
      }
    }
  });

  return (
    <>
      {elements.map((element, index) => (
         
        <React.Fragment key={`denotation-part-${index}`}>
          {element}
          {index + 1 !== elements.length && (' ')}
        </React.Fragment>
      ))}
    </>
  );
}

export default Denotation;
